import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import { type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { QuoteMatchMap } from "../gradeValidationHelpers.js";
import type { TranscriptSearchNavState } from "../gradingHelpers.js";
import type { GradingTranscriptWithIndexHandle } from "../gradingTranscriptWithIndex.js";
import {
  buildSearchSnippetMatches,
  type SearchNavTarget,
} from "./searchSnippetMatches.js";

/**
 * Highlights the current transcript-search match and steps through the matches.
 */
export function useTranscriptSearchNavigation(params: {
  recordings: readonly AccountGradingRecording[];
  navState: TranscriptSearchNavState | undefined;
  transcriptRef: RefObject<GradingTranscriptWithIndexHandle | null>;
  transcriptContainerRef: RefObject<HTMLElement | null>;
}) {
  const { recordings, navState, transcriptRef, transcriptContainerRef } =
    params;

  const { searchMatches: allSearchMatches, navTargets } = useMemo((): {
    searchMatches: QuoteMatchMap;
    navTargets: SearchNavTarget[];
  } => {
    if (!navState || recordings.length === 0) {
      return {
        searchMatches: QuoteMatchMap.fromMatches([]),
        navTargets: [],
      };
    }
    return buildSearchSnippetMatches({
      recordings,
      snippets: navState.matches,
    });
  }, [navState, recordings]);

  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  // Only the current match is highlighted; the rest stay plain until paged to.
  const searchMatches = useMemo(() => {
    const quoteId = dismissed
      ? undefined
      : navTargets[currentMatchIndex]?.quoteId;
    const spans = quoteId
      ? QuoteMatchMap.findByQuote(allSearchMatches, quoteId)
      : undefined;
    return QuoteMatchMap.fromMatches(spans ?? []);
  }, [allSearchMatches, navTargets, currentMatchIndex, dismissed]);

  const focusTarget = useEffectEvent((index: number) => {
    const target = navTargets[index];
    const container = transcriptContainerRef.current;
    if (!target || !container) {
      return;
    }
    transcriptRef.current?.focusQuote({
      container,
      quoteId: target.quoteId,
      highlightQuote: true,
    });
  });

  // Auto-focus the first match once matches exist (once per mount).
  const didInitRef = useRef(false);
  useEffect(() => {
    if (didInitRef.current || navTargets.length === 0) {
      return;
    }
    didInitRef.current = true;
    setCurrentMatchIndex(0);
    focusTarget(0);
  }, [navTargets, focusTarget]);

  // Focus after paging, once the render that swaps the current-only highlight
  // has committed — `focusQuote` resolves the quote id via the transcript's
  // `searchMatches` prop, which only contains the new match after that render.
  useEffect(() => {
    focusTarget(currentMatchIndex);
  }, [currentMatchIndex, focusTarget]);

  const goTo = (index: number) => {
    setCurrentMatchIndex(Math.max(0, Math.min(index, navTargets.length - 1)));
  };

  return {
    searchMatches,
    count: dismissed ? 0 : navTargets.length,
    currentMatchIndex,
    next: () => goTo(currentMatchIndex + 1),
    prev: () => goTo(currentMatchIndex - 1),
    close: () => setDismissed(true),
  };
}
